import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatCard, StatusPill, Loader, NameHint } from "@/components/ui-bits";
import { useNameLookup } from "@/hooks/useNameLookup";
import { Wallet, PiggyBank, Crown, ArrowRight, Loader2, Send, Copy, QrCode, Upload, Clock, X, Sparkles, Gem, Lock } from "lucide-react";

export default function Wallets() {
  const [w, setW] = useState(null);
  const [settings, setSettings] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [moveAmt, setMoveAmt] = useState("");
  const [pp, setPp] = useState({ to_referral_code: "", amount: "" });
  const [sp, setSp] = useState({ to_referral_code: "", amount: "" });
  const [busy, setBusy] = useState("");
  const [showDeposit, setShowDeposit] = useState(false);
  const [dep, setDep] = useState({ amount: "", tx_hash: "", target: "deposit" });
  const [proof, setProof] = useState(null);
  const [proofPath, setProofPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const ppLookup = useNameLookup(pp.to_referral_code);
  const spLookup = useNameLookup(sp.to_referral_code);

  const load = () => {
    API.get("/wallets").then((r) => setW(r.data));
    API.get("/deposits").then((r) => setDeposits(r.data.deposits));
  };
  useEffect(() => {
    load();
    API.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const copyAddr = () => { navigator.clipboard.writeText(settings.payment_wallet_address); toast.success("Address copied"); };

  const uploadProof = async (file) => {
    if (!file) return;
    setProof(file);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await API.post("/upload/proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofPath(r.data.proof_path);
      toast.success("Proof uploaded");
    } catch (e) { toast.error(apiError(e)); setProof(null); } finally { setUploading(false); }
  };

  const submitDeposit = async () => {
    if (!proofPath) return toast.error("Please upload your payment proof");
    setBusy("deposit");
    try {
      const r = await API.post("/deposit", { amount: parseFloat(dep.amount), tx_hash: dep.tx_hash, proof_path: proofPath, target: dep.target });
      toast.success(r.data.message);
      setShowDeposit(false); setDep({ amount: "", tx_hash: "", target: "deposit" }); setProof(null); setProofPath("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const moveToDeposit = async () => {
    setBusy("move");
    try { const r = await API.post("/wallet/transfer-to-deposit", { amount: parseFloat(moveAmt) }); toast.success(r.data.message); setMoveAmt(""); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const ppTransfer = async () => {
    setBusy("pp");
    try { const r = await API.post("/pp-transfer", { to_referral_code: pp.to_referral_code, amount: parseFloat(pp.amount) }); toast.success(r.data.message); setPp({ to_referral_code: "", amount: "" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const silverTransfer = async () => {
    setBusy("sp");
    try { const r = await API.post("/silver-transfer", { to_referral_code: sp.to_referral_code, amount: parseFloat(sp.amount) }); toast.success(r.data.message); setSp({ to_referral_code: "", amount: "" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!w || !settings) return <Loader label="Loading wallets" />;
  const field = "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white outline-none focus:border-emerald-500/50";
  const pending = deposits.filter((d) => d.status === "pending").length;

  return (
    <div>
      <PageHeader title="Wallets" subtitle="Add funds via crypto (BNB / BEP-20), then admin approves your payment. Manage Main, Top-Up, Deposit & Creator balances." testid="wallets-header"
        right={
          <button data-testid="open-deposit-btn" onClick={() => setShowDeposit(true)}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold px-5 py-2.5 rounded-xl">
            <QrCode className="h-4 w-4" /> Add Funds
          </button>
        }
      />

      {pending > 0 && (
        <div className="glass rounded-2xl p-4 mb-6 flex items-center gap-3 text-sm text-amber-300" data-testid="pending-banner">
          <Clock className="h-5 w-5" /> You have {pending} payment(s) pending admin approval. Your funds will be credited once approved.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="w-main" label="Main Wallet" value={w.main_wallet} money icon={Wallet} accent="emerald" sub={w.main_active ? "Unlocked · move/withdraw enabled" : "Locked · drop a Silver Card to unlock"} />
        <StatCard testid="w-topup" label="Top-Up Wallet" value={w.topup_wallet} money icon={PiggyBank} accent="cyan" sub="Package activation · 20% of earnings" />
        <StatCard testid="w-silver" label="Silver Card Wallet" value={w.silver_card_wallet} money icon={Gem} accent="gold" sub="Buy & drop Silver Cards" />
        <StatCard testid="w-deposit" label="Deposit Wallet (P/P)" value={w.deposit_wallet} money icon={Send} accent="violet" sub="Fund downline / re-top-up" />
      </div>

      {!w.main_active && (
        <div className="glass rounded-2xl p-4 mb-6 flex items-center gap-3 text-sm text-rose-300" data-testid="main-locked-banner">
          <Lock className="h-5 w-5 shrink-0" />
          Your Main wallet is <b>locked</b>. You can't move funds out or withdraw until you drop a Silver Card (unlocks it for 6 hours). Funds can still flow IN. Go to the Silver Card page to drop.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold text-white mb-1">Main → Deposit</h3>
          <p className="text-xs text-slate-500 mb-4">Move funds into the Deposit (P/P) wallet. Min $3, multiples of $3. A 15% fee is charged from your Main wallet.</p>
          <input data-testid="move-amount" type="number" min="0" className={field} placeholder="Amount (USD)" value={moveAmt} onChange={(e) => setMoveAmt(e.target.value)} />
          <button data-testid="move-submit" disabled={busy === "move" || !moveAmt} onClick={moveToDeposit}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-white/15 hover:border-emerald-500/40 text-white font-semibold py-3 rounded-xl">
            {busy === "move" && <Loader2 className="h-4 w-4 animate-spin" />} <ArrowRight className="h-4 w-4" /> Transfer
          </button>
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold text-white mb-1">Deposit P/P Transfer</h3>
          <p className="text-xs text-slate-500 mb-4">Send from your Deposit wallet to any downline or upline member's Deposit wallet. No cross-line. Min $1. No fee.</p>
          <input data-testid="pp-code" className={`${field} mb-1`} placeholder="Downline / Upline User ID" value={pp.to_referral_code} onChange={(e) => setPp({ ...pp, to_referral_code: e.target.value })} />
          <NameHint state={ppLookup} testid="pp-name" />
          <input data-testid="pp-amount" type="number" min="0" className={`${field} mt-3`} placeholder="Amount (USD)" value={pp.amount} onChange={(e) => setPp({ ...pp, amount: e.target.value })} />
          <button data-testid="pp-submit" disabled={busy === "pp" || !pp.amount || !pp.to_referral_code} onClick={ppTransfer}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-white/15 hover:border-violet-500/40 text-white font-semibold py-3 rounded-xl">
            {busy === "pp" && <Loader2 className="h-4 w-4 animate-spin" />} <Send className="h-4 w-4" /> Send
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold text-white mb-1">Silver Card P/P Transfer</h3>
          <p className="text-xs text-slate-500 mb-4">Send from your Silver Card wallet to a downline member's Silver Card wallet only. No upline, no cross-line. Min $1. No fee.</p>
          <input data-testid="sp-code" className={`${field} mb-1`} placeholder="Downline User ID" value={sp.to_referral_code} onChange={(e) => setSp({ ...sp, to_referral_code: e.target.value })} />
          <NameHint state={spLookup} testid="sp-name" />
          <input data-testid="sp-amount" type="number" min="0" className={`${field} mt-3`} placeholder="Amount (USD)" value={sp.amount} onChange={(e) => setSp({ ...sp, amount: e.target.value })} />
          <button data-testid="sp-submit" disabled={busy === "sp" || !sp.amount || !sp.to_referral_code} onClick={silverTransfer}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-white/15 hover:border-cyan-500/40 text-white font-semibold py-3 rounded-xl">
            {busy === "sp" && <Loader2 className="h-4 w-4 animate-spin" />} <Gem className="h-4 w-4" /> Send
          </button>
        </div>
      </div>

      {/* Deposit history */}
      <div className="glass rounded-2xl p-6" data-testid="deposit-history">
        <h3 className="font-display text-lg font-semibold text-white mb-4">Payment Requests</h3>
        {deposits.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">No payment requests yet. Click "Add Funds" to deposit.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Tx Hash</th><th className="py-2 text-right">Status</th>
              </tr></thead>
              <tbody>
                {deposits.map((d) => (
                  <tr key={d.id} className="border-b border-white/5 last:border-0">
                    <td className="py-3 pr-4 font-num text-slate-400">{new Date(d.created_at).toLocaleDateString()}</td>
                    <td className="py-3 pr-4 font-num text-white">{fmt(d.amount)}</td>
                    <td className="py-3 pr-4 font-num text-slate-500 text-xs truncate max-w-[160px]">{d.tx_hash || "—"}</td>
                    <td className="py-3 text-right"><StatusPill status={d.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deposit modal */}
      {showDeposit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto" data-testid="deposit-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowDeposit(false)} />
          <div className="relative glass rounded-2xl p-6 w-full max-w-lg my-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold text-white">Add Funds via {settings.payment_coin}</h3>
              <button onClick={() => setShowDeposit(false)} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs p-3 mb-4">
              {settings.payment_instructions}
            </div>

            <div className="flex flex-col items-center mb-4">
              <div className="bg-white p-3 rounded-xl">
                <QRCodeCanvas value={settings.payment_wallet_address} size={168} data-testid="deposit-qr" />
              </div>
              <div className="text-[0.65rem] uppercase tracking-wide text-slate-500 mt-3">{settings.payment_network}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-num text-xs text-emerald-300 break-all text-center" data-testid="deposit-address">{settings.payment_wallet_address}</span>
                <button data-testid="copy-address" onClick={copyAddr} className="text-slate-400 hover:text-white shrink-0"><Copy className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400">Fund which wallet?</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <button type="button" data-testid="deposit-target-deposit" onClick={() => setDep({ ...dep, target: "deposit" })}
                    className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${dep.target === "deposit" ? "bg-emerald-500 text-slate-900 border-emerald-500" : "bg-white/5 text-slate-300 border-white/10 hover:border-emerald-500/40"}`}>
                    Deposit Wallet
                  </button>
                  <button type="button" data-testid="deposit-target-silver" onClick={() => setDep({ ...dep, target: "silver_card" })}
                    className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${dep.target === "silver_card" ? "bg-cyan-500 text-slate-900 border-cyan-500" : "bg-white/5 text-slate-300 border-white/10 hover:border-cyan-500/40"}`}>
                    Silver Card Wallet
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400">Amount paid (USD)</label>
                <input data-testid="deposit-amount" type="number" min="0" className={field} placeholder={`Min $${settings.min_fund_request}`} value={dep.amount} onChange={(e) => setDep({ ...dep, amount: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-slate-400">Transaction hash (optional)</label>
                <input data-testid="deposit-txhash" className={field} placeholder="0x..." value={dep.tx_hash} onChange={(e) => setDep({ ...dep, tx_hash: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-slate-400">Payment proof (screenshot)</label>
                <label data-testid="proof-dropzone" className="mt-1 flex items-center justify-center gap-2 border border-dashed border-white/20 hover:border-emerald-500/40 rounded-xl py-4 cursor-pointer text-slate-400 text-sm">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {proofPath ? "Proof uploaded ✓" : proof ? proof.name : "Click to upload proof image"}
                  <input data-testid="proof-input" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => uploadProof(e.target.files[0])} />
                </label>
              </div>
            </div>

            <button data-testid="deposit-submit" disabled={busy === "deposit" || uploading || !dep.amount || !proofPath} onClick={submitDeposit}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-900 font-semibold py-3 rounded-xl">
              {busy === "deposit" && <Loader2 className="h-4 w-4 animate-spin" />} Submit for Approval
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
